import {
  analyticsClient,
  type KeyboardNavigationActionFamily,
  getDatabaseContainerType,
  getSelectionsAnalyticsAttributes,
} from '@databases/analytics';

export interface KeyboardNavigationUsageTracker {
  /** Records eligibility once for this mounted bridge database identity. */
  markAvailable(): void;
  /** Records the first accepted use of each semantic action family. */
  record(actionFamily: KeyboardNavigationActionFamily): void;
  /** Prevents future records and releases the lifecycle-local dedupe set. */
  dispose(): void;
}

/**
 * Creates bounded keyboard-navigation product analytics for one mounted bridge
 * database identity. The tracker must outlive permission/view machine rebuilds
 * and be disposed when that outer identity is recycled.
 */
export function createKeyboardNavigationUsageTracker({
  contentId,
}: {
  contentId: string;
}): KeyboardNavigationUsageTracker {
  let isAvailable = false;
  let isDisposed = false;
  const recordedFamilies = new Set<KeyboardNavigationActionFamily>();

  function getCommonEventData() {
    return {
      contentId,
      databaseContainer: getDatabaseContainerType(),
      ...getSelectionsAnalyticsAttributes(contentId),
    };
  }

  function send(
    event:
      | { kind: 'availability' }
      | { kind: 'usage'; actionFamily: KeyboardNavigationActionFamily },
  ) {
    const eventData = {
      ...getCommonEventData(),
      ...event,
    };

    queueMicrotask(() => {
      void analyticsClient.sendKeyboardNavigationEvent(eventData).catch(() => undefined);
    });
  }

  return {
    markAvailable() {
      if (isDisposed || isAvailable) {
        return;
      }

      isAvailable = true;
      send({ kind: 'availability' });
    },

    record(actionFamily) {
      if (isDisposed || !isAvailable || recordedFamilies.has(actionFamily)) {
        return;
      }

      recordedFamilies.add(actionFamily);
      send({ kind: 'usage', actionFamily });
    },

    dispose() {
      isDisposed = true;
      recordedFamilies.clear();
    },
  };
}
